// frontend/src/lib/wo-cache.js
// Module-level cache for work orders — shared across all page components.
// Eliminates redundant /work-orders fetches when navigating between pages
// within the same session.
import { apiFetch, waitForAuthUser } from "./api";

const WO_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

let _cache = null;
let _expiry = 0;
let _inflightPromise = null; // deduplicates concurrent calls during the same fetch

/**
 * Returns all work orders, using the module-level cache when fresh.
 * Concurrent callers during the same fetch window share a single HTTP request.
 *
 * @param {boolean} forceRefresh - Skip cache and re-fetch.
 * @returns {Promise<Array>} Array of work order objects.
 */
export async function getWorkOrders(forceRefresh = false) {
  // Return cache if still fresh
  if (!forceRefresh && _cache && Date.now() < _expiry) {
    return _cache;
  }

  // Deduplicate: if a fetch is already in-flight, wait for it
  if (_inflightPromise) {
    return _inflightPromise;
  }

  _inflightPromise = waitForAuthUser()
    .then(() => apiFetch("/work-orders"))
    .then(async (res) => {
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = payload?.detail;
        const detail = typeof d === "string"
          ? d
          : (d && typeof d === "object" && d.message) ? d.message : (payload?.message || res.statusText || "Request failed");
        throw new Error(`${res.status}: ${detail}`);
      }
      const data = payload.data ?? payload;
      _cache = data;
      _expiry = Date.now() + WO_CACHE_TTL_MS;
      return data;
    })
    .finally(() => {
      _inflightPromise = null;
    });

  return _inflightPromise;
}

/**
 * Invalidate the work orders cache. Call after any WO create, update, or
 * status change so the next consumer gets fresh data.
 */
export function invalidateWorkOrders() {
  _cache = null;
  _expiry = 0;
}
